import PlaceIcon from '@mui/icons-material/Place';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Terrain } from '../instanceTypes';
import { getTerrainLabel } from '../utils/terrain';

interface TerrainChipsProps {
  codes: string[] | null | undefined;
  /**
   * Cap the number of badges shown; any extra terrains collapse into a single
   * "+N" overflow badge (tooltip lists them). Used by the instances table column
   * so a terrain-heavy instance never blows up the row height (HOMCO-1114).
   * Left unset (e.g. on the detail view) shows every terrain.
   */
  maxVisible?: number;
  /** When provided, codes are resolved to full labels (codeterrain – pays – libellé). */
  terrainMap?: Map<string, Terrain>;
}

export function TerrainChips({ codes, maxVisible, terrainMap }: TerrainChipsProps) {
  const theme = useTheme();
  const unique = [...new Set(codes ?? [])];
  const cap = maxVisible ?? unique.length;
  const visible = unique.slice(0, cap);
  const hidden = unique.slice(cap);

  const resolveLabel = (code: string): string => {
    const terrain = terrainMap?.get(code);
    return terrain ? getTerrainLabel(terrain) : code;
  };

  const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    height: 26,
    px: '12px',
    borderRadius: theme.borderRadius.pill,
  } as const;

  const labelSx = {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightSemiBold,
    letterSpacing: '0.01em',
    lineHeight: 1,
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: maxVisible == null ? 'wrap' : 'nowrap',
        gap: '6px',
        alignItems: 'center',
      }}
    >
      {visible.map(code => (
        <Box
          key={code}
          sx={{
            ...chipSx,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
          }}
        >
          <PlaceIcon
            sx={{
              fontSize: theme.typography.caption.fontSize,
              color: theme.palette.primary.main,
              opacity: 0.8,
            }}
          />
          <Typography sx={{ ...labelSx, color: theme.palette.primary.main }}>
            {resolveLabel(code)}
          </Typography>
        </Box>
      ))}

      {hidden.length > 0 && (
        <Tooltip title={hidden.map(resolveLabel).join(', ')} placement="top">
          <Box
            aria-label={`+${hidden.length}`}
            sx={{
              ...chipSx,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
              cursor: 'default',
            }}
          >
            <Typography sx={{ ...labelSx, color: theme.palette.primary.main }}>
              +{hidden.length}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
